/** Scope 3's Categories tab: the fifteen categories, a decision for each (gate 19, S3-01, S18).
 *
 * One bordered grid in the standard's own order. **No quantity appears in a row, ever**: the decision
 * is a word, and an unassessed category is the words *Not assessed* and nothing beside them, because
 * a category nobody has looked at is not a category with nothing in it (ACC-09). The standard
 * requires every category to be accounted for and any exclusion to be justified (verification log
 * S-08), and a grid that did not separate "nobody has looked" from "looked and excluded, because..."
 * would show a compliant-looking inventory built on work nobody did.
 *
 * Every sentence about the regime is the server's - the justification each category carries, the
 * census sentence and the method packs - and none is typed here. The census sentence and the packs
 * sit behind two buttons in the filter line (a drawer each), the way every long explanation does
 * (ADR-0212 decision 2); the sentence is shown verbatim, never reworded.
 *
 * **Recording a decision** is an Approver's or an Admin's act (ADR-0008) and opens in a side drawer
 * (`DecisionForm`, unchanged): exactly one of the control and the reason there is none is on screen
 * once the session has loaded (design rule 6) - a chip in the filter line says whose act it is, or
 * that a reporting year must be chosen. **Add** is category 3's alone (transmission and distribution
 * loss, the one thing the engine prices); the other fourteen say *Not available yet*.
 */

import { Truck } from "@phosphor-icons/react";
import { useState } from "react";

import { useMe } from "../api/auth";
import {
  EXPECTED_CATEGORIES,
  inOrder,
  isDeclaredYear,
  mayDecide,
  provenanceOf,
  relevanceLabel,
  scope3CensusIsValid,
} from "../api/scope3";
import type { Scope3Category } from "../api/scope3";
import { ReadFailed } from "../components/Loaded";
import { WindowLockBanner } from "../components/WindowLockBanner";
import type { FilterState, GridColumn } from "../components/ui";
import {
  AddImportActions,
  Drawer,
  EMPTY_FILTERS,
  EmptyState,
  FilterLine,
  GridTable,
  InfoPopover,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import { quantity } from "../lib/format";
import { DASH } from "./scopeTable/context";
import type { Scope3Shared } from "./scopeTable/context";
import { DecidedBy, DecisionForm } from "./Scope3Decision";
import own from "./Scope3.module.css";

/** Transmission and distribution loss is the only thing the engine prices for Scope 3, and it is
 * category 3's (verification log S-08, the standard's Table 5.3). */
const PRICEABLE_CATEGORY = 3;

export function Scope3Categories({ screen, period, role, inventoryRead, onAdd, onImport }: Scope3Shared) {
  const meRead = useMe();
  const coverage = inventoryRead.data?.scope3 ?? null;
  const may = mayDecide(meRead.data?.role);
  const yearIsDeclared = isDeclaredYear(screen.organization?.periods, period.period_end);
  const [deciding, setDeciding] = useState<number | null>(null);
  const [recorded, setRecorded] = useState<{ message: string; already: boolean } | null>(null);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sentenceOpen, setSentenceOpen] = useState(false);
  const [packsOpen, setPacksOpen] = useState(false);

  const stream = filters.values["stream"]?.[0] ?? "all";
  const needle = filters.search.trim().toLowerCase();
  const all = coverage ? inOrder(coverage.categories) : [];
  const rows = all.filter(
    (category) =>
      (stream === "all" || category.stream === stream) &&
      (needle === "" ||
        `${category.name} ${relevanceLabel(category.relevance)} ${category.stream}`.toLowerCase().includes(needle)),
  );
  const count = (relevance: string) => all.filter((category) => category.relevance === relevance).length;
  const dash = inventoryRead.isPending || Boolean(inventoryRead.error) || coverage === null;

  // **The category the open form is about, read fresh each render**, so a re-read under an open form
  // (the stale-decision conflict) shows what is now on record.
  const open = deciding === null ? undefined : all.find((category) => category.number === deciding);

  const columns: GridColumn<Scope3Category>[] = [
    { id: "number", header: "No.", numeric: true, cell: (category) => String(category.number) },
    { id: "name", header: "Category", rowHeader: true, wrap: true, cell: (category) => category.name },
    { id: "stream", header: "Stream", cell: (category) => category.stream },
    {
      id: "decision",
      header: "Decision",
      cell: (category) => (
        <span
          className={category.relevance === "not_assessed" ? own.unassessed : undefined}
          data-testid={`scope3-decision-${category.number}`}
        >
          {relevanceLabel(category.relevance)}
        </span>
      ),
    },
    {
      id: "basis",
      header: "Why, and how",
      wrap: true,
      cell: (category) => (
        <span className={own.basis}>
          {category.justification ? <span>{category.justification}</span> : null}
          {category.method ? <span className={own.method}>Method: {category.method}</span> : null}
          <DecidedBy category={category} provenance={provenanceOf(category, meRead.data?.user_id)} />
        </span>
      ),
    },
    {
      id: "add",
      header: "Add data",
      cell: (category) =>
        category.number === PRICEABLE_CATEGORY ? (
          role !== undefined && (role === "admin" || role === "approver" || role === "contributor") ? (
            <button type="button" className={own.open} data-testid="scope3-add-transmission-loss" onClick={onAdd}>
              Add transmission loss
            </button>
          ) : (
            "Transmission loss"
          )
        ) : (
          <span className={own.unassessed} data-testid={`scope3-add-unavailable-${category.number}`}>
            Not available yet
          </span>
        ),
    },
    ...(may && yearIsDeclared
      ? [
          {
            id: "decide",
            header: "Decide",
            cell: (category: Scope3Category) => (
              <button
                type="button"
                className={own.open}
                data-testid={`scope3-decide-open-${category.number}`}
                aria-expanded={deciding === category.number}
                aria-label={`${
                  category.relevance === "not_assessed" ? "Decide" : "Change the decision for"
                } category ${String(category.number)}, ${category.name}`}
                onClick={() => {
                  setRecorded(null);
                  setDeciding(deciding === category.number ? null : category.number);
                }}
              >
                {category.relevance === "not_assessed" ? "Decide" : "Change"}
              </button>
            ),
          } satisfies GridColumn<Scope3Category>,
        ]
      : []),
  ];

  const alert =
    coverage !== null &&
    (coverage.categories.length !== EXPECTED_CATEGORIES || !scope3CensusIsValid(coverage.categories)) ? (
      <p className={own.warn} role="alert" data-testid="scope3-census-invalid">
        The returned list does not contain each category from 1 to {EXPECTED_CATEGORIES} exactly once (EmitIQ
        returned {coverage.categories.length}). Treat it as incomplete.
      </p>
    ) : null;

  const who =
    may === false ? (
      <StatusChip tone="neutral" size="sm" testId="scope3-decide-who-can">
        An Approver or an Admin decides
      </StatusChip>
    ) : may && !yearIsDeclared ? (
      <StatusChip tone="neutral" size="sm" testId="scope3-decide-no-year">
        Choose a reporting year to decide
      </StatusChip>
    ) : null;

  const lock = inventoryRead.data?.window_lock;

  const stateNode =
    inventoryRead.error ? (
      <ReadFailed
        of="The Scope 3 categories"
        error={inventoryRead.error}
        onRetry={() => void inventoryRead.refetch()}
      />
    ) : coverage === null && !inventoryRead.isPending ? (
      <p className={own.note} data-testid="scope3-no-coverage">
        This workspace has no company recorded, so there is nowhere to keep a Scope 3 decision.
      </p>
    ) : undefined;

  return (
    <TablePage
      icon={Truck}
      title="Scope 3"
      actions={<AddImportActions role={role} onAdd={onAdd} onImport={onImport} />}
      tiles={
        <>
          <WindowLockBanner lock={lock} />
          {alert}
          <TileStrip label="Scope 3 categories">
            <Tile
              label="Assessed"
              value={dash ? DASH : quantity(String(coverage.assessed))}
              infoKey="scope3.decisions"
              testId="scope3-stat-assessed"
            />
            <Tile label="Relevant" value={dash ? DASH : String(count("relevant"))} testId="scope3-stat-relevant" />
            <Tile
              label="Not applicable"
              value={dash ? DASH : String(count("not_applicable"))}
              testId="scope3-stat-not-applicable"
            />
            <Tile label="Excluded" value={dash ? DASH : String(count("excluded"))} testId="scope3-stat-excluded" />
            <Tile
              label="Not assessed"
              value={dash ? DASH : String(count("not_assessed"))}
              testId="scope3-stat-not-assessed"
            />
          </TileStrip>
        </>
      }
      state={stateNode}
      filters={stateNode ? undefined : (
      <FilterLine
        filters={[
          {
            id: "stream",
            label: "Stream",
            mode: "single",
            allLabel: "All streams",
            options: [
              { value: "upstream", label: "Upstream" },
              { value: "downstream", label: "Downstream" },
            ],
          },
        ]}
        state={filters}
        onChange={(next) => {
          setFilters(next);
          setDeciding(null);
        }}
        searchLabel="Search this table"
        resultCount={{ shown: rows.length, total: all.length, noun: "categories" }}
        actions={
          <>
            {recorded ? (
              <span role="status" data-testid="scope3-decided" data-already-recorded={recorded.already}>
                <StatusChip tone="ok" size="sm">
                  {recorded.message}
                </StatusChip>
              </span>
            ) : null}
            {who}
            {coverage ? (
              <button type="button" className={own.open} onClick={() => setSentenceOpen(true)}>
                About these categories
              </button>
            ) : null}
            {coverage?.method_packs?.length ? (
              <button type="button" className={own.open} onClick={() => setPacksOpen(true)}>
                Methodologies
              </button>
            ) : null}
            <InfoPopover infoKey="scope3.categories" />
          </>
        }
      />
      )}
      table={
        <>
          <GridTable
            caption="Scope 3 categories and the decision recorded for each"
            columns={columns}
            rows={rows}
            rowKey={(category) => String(category.number)}
            rowLabel={(category) => `category ${String(category.number)}, ${category.name}`}
            // The category's own number is its column; a row count beside it would say it twice.
            rowNumbers={false}
            rowAttributes={(category) => ({
              "data-testid": `scope3-category-${category.number}`,
              "data-relevance": category.relevance,
            })}
            loading={inventoryRead.isPending}
            empty={<EmptyState title="No category matches." compact />}
            testId="scope3-categories"
          />

          <Drawer
            open={sentenceOpen}
            onClose={() => setSentenceOpen(false)}
            title="About these categories"
            width="sm"
          >
            {/* The server's own line. It never names a figure - a property of the sentence, asserted on
                the backend - so there is no number here to mistake for a total. */}
            <p data-testid="scope3-sentence">{coverage?.sentence}</p>
          </Drawer>

          <Drawer open={packsOpen} onClose={() => setPacksOpen(false)} title="Sector methodologies" width="md">
            <div data-testid="scope3-method-packs">
              {coverage?.method_packs?.map((pack) => (
                <section key={pack.key}>
                  <h3>{pack.label}</h3>
                  <p>{pack.supported ? "Method supported" : "Method not supported"}</p>
                  <p>{pack.prerequisite}</p>
                  <ul>
                    {pack.classes.map((item) => (
                      <li key={item.key}>
                        <strong>{item.name}</strong>
                        <p>
                          {item.obligation.replace(/_/g, " ")} · {item.supported ? "Supported" : "Not supported"}
                        </p>
                        <p>{item.limitation}</p>
                        <a href={item.source_url} target="_blank" rel="noreferrer">
                          {item.citation}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </Drawer>

          <Drawer
            open={open !== undefined}
            onClose={() => setDeciding(null)}
            title={open ? `Category ${String(open.number)}, ${open.name}` : "Category"}
            width="md"
            testId="scope3-decide-drawer"
          >
            {open ? (
              <div data-testid={`scope3-decide-row-${open.number}`}>
                <DecisionForm
                  category={open}
                  yearEnd={period.period_end}
                  meId={meRead.data?.user_id}
                  refreshing={inventoryRead.isFetching}
                  onDone={(done) => {
                    setRecorded(done);
                    setDeciding(null);
                  }}
                  onCancel={() => setDeciding(null)}
                  onReload={() => void inventoryRead.refetch()}
                />
              </div>
            ) : null}
          </Drawer>
        </>
      }
    />
  );
}
